'use client';

import * as React from 'react';
import { Sparkles } from 'lucide-react';
import Link from 'next/link';
import { useUnreadAnnouncements } from '@/api/queries/announcements.queries';
import { WHATS_NEW_HREF } from '@/components/layout/whats-new/constants';
import { useWhatsNewEnabled } from '@/components/layout/whats-new/use-whats-new-enabled';
import {
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useChromePathname } from '@/shared/components/providers/chrome-pathname';
import { isUnreadAnnouncement } from '@/shared/lib/announcements/announcement-rules';
import { useAuthStore } from '@/shared/stores/auth-store';
import { NewBadge } from './new-badge';

/**
 * "What's New" entry pinned to the sidebar bottom, with a New marker while
 * anything is unread. Hidden with the rest of What's New (flag + subscriber
 * gate). Mount-gated: the gate reads the persisted auth store, so the first
 * client render must match the server's (empty) markup.
 */
export function NavWhatsNew() {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);
  const pathname = useChromePathname();
  const { enabled } = useWhatsNewEnabled();
  const { data } = useUnreadAnnouncements(enabled);
  const readAt = useAuthStore((s) => s.user?.last_announcements_read_at ?? null);

  if (!mounted || !enabled) return null;

  const hasUnread = (data?.announcements ?? []).some((a) =>
    isUnreadAnnouncement(a.published_at, readAt)
  );
  const isActive = pathname === WHATS_NEW_HREF;

  return (
    <SidebarFooter>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton asChild isActive={isActive} tooltip="What's New">
            <Link href={WHATS_NEW_HREF} className="relative">
              <Sparkles />
              <span>What&apos;s New</span>
              {hasUnread && <NewBadge />}
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>
  );
}
